import {
  formatCompactDuration,
  formatSessionShiftLabel,
} from "../../helpers/dateTimeHelpers";

/**
 * Pure presentation helpers for the Time Logs record language (hub rows,
 * details, and later Project → Labour). No data access, no business rules:
 * stored total_hours / net_work_seconds / payroll_status are only formatted.
 */

/** Hours for display. total_hours is what the previous cards showed. */
export function logHours(log) {
  const stored = Number(log?.total_hours);
  if (log?.total_hours != null && log?.total_hours !== "" && Number.isFinite(stored)) {
    return stored;
  }
  const net = Number(log?.net_work_seconds);
  if (log?.net_work_seconds != null && Number.isFinite(net)) {
    return net / 3600;
  }
  return 0;
}

/** "19h 22m", "3m", "0m". */
export function formatLogDuration(log) {
  return formatCompactDuration(logHours(log));
}

/** Clock range ("2:11 AM – 9:33 PM") or "Sep 14, 10:00 PM → Sep 15, 2:00 AM" across dates. */
export function formatLogRange(log) {
  if (!log?.start_time || !log?.end_time) {
    return "";
  }
  const label = formatSessionShiftLabel(log.start_time, log.end_time);
  return label === "—" ? "" : label;
}

export function logDateIso(log) {
  if (log?.date_iso) return log.date_iso;
  if (log?.start_time) return String(log.start_time).substring(0, 10);
  return "";
}

function parseIsoDate(dateIso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateIso || ""));
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** "Tue, Oct 6, 2026" from the stored payroll date (no timezone shift). */
export function formatLogDay(dateIso, { long = true } = {}) {
  const date = parseIsoDate(dateIso);
  if (!date) return "Unknown date";
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(long ? { year: "numeric" } : {}),
  });
}

export function pluralLogs(count) {
  return `${count} ${count === 1 ? "log" : "logs"}`;
}

export function summarizeLogs(logs = []) {
  const hours = logs.reduce((sum, log) => sum + logHours(log), 0);
  return { count: logs.length, hours, durationLabel: formatCompactDuration(hours) };
}

/** Chronological groups keyed by the stored date, in the order given. */
export function groupLogsByDate(logs = []) {
  const groups = [];
  const index = new Map();
  logs.forEach((log) => {
    const key = logDateIso(log);
    if (!index.has(key)) {
      const group = { dateIso: key, label: formatLogDay(key), logs: [] };
      index.set(key, group);
      groups.push(group);
    }
    index.get(key).logs.push(log);
  });
  return groups.map((group) => ({ ...group, ...summarizeLogs(group.logs) }));
}

/** Payroll state as stored: anything other than "paid" reads as Unpaid. */
export function payrollPill(log) {
  const paid = String(log?.payroll_status || "").toLowerCase() === "paid";
  return { label: paid ? "Paid" : "Unpaid", tone: paid ? "ok" : "gold" };
}

export function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatLogTimestamp(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
